import { useEffect, useState, useMemo } from "react";
import { Link, useParams, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import {
  FileSignature, CheckCircle2, FileText, ArrowLeft, Eye, AlertCircle,
  Send, Loader2, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VerificaModuliPage() {
  const { praticaId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [checked, setChecked] = useState({});  // modulo key -> visto

  useEffect(() => {
    if (!token) {
      setError("Token mancante. Torna al wizard per inviare nuovamente la pratica.");
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/credito/pratica/${praticaId}/moduli`, { params: { token } });
        setData(data);
        if (data?.moduli?.length) setActive(data.moduli[0].key);
      } catch (e) {
        setError(e?.response?.data?.detail || "Errore caricamento moduli");
      } finally {
        setLoading(false);
      }
    })();
  }, [praticaId, token]);

  const allChecked = useMemo(() => {
    if (!data?.moduli) return false;
    return data.moduli.every((m) => checked[m.key]);
  }, [data, checked]);

  const previewUrl = (key) => `${API}/credito/pratica/${praticaId}/moduli/${key}?token=${encodeURIComponent(token)}`;

  const toggleChecked = (key) => setChecked((s) => ({ ...s, [key]: !s[key] }));

  const submit = async () => {
    setConfirming(true);
    try {
      await axios.post(`${API}/credito/pratica/${praticaId}/conferma-firma`, null, { params: { token } });
      setConfirmed(true);
      toast.success("Moduli verificati, ti stiamo reindirizzando alla pagina di firma...");
      // Naviga alla thank-you page (polling automatico dello stato firma)
      setTimeout(() => {
        navigate(`/credito/pratica/${encodeURIComponent(praticaId)}/completata?token=${encodeURIComponent(token)}`);
      }, 1200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante la conferma");
    } finally {
      setConfirming(false);
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-[#FCFCFA] pt-24 pb-16 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#163321] mx-auto mb-3" />
            <p className="text-sm text-[#4A5E51]">Caricamento moduli precompilati...</p>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-[#FCFCFA] pt-24 pb-16">
          <div className="max-w-xl mx-auto px-5 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h1 className="font-heading text-2xl text-[#0F1F14] font-medium mb-3">Impossibile accedere alla verifica</h1>
            <p className="text-sm text-[#4A5E51] mb-6">{error}</p>
            <Button onClick={() => navigate("/credito/pratica")} className="rounded-full bg-[#163321] text-white">
              Nuova pratica
            </Button>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  if (confirmed) {
    return <SuccessConfirm praticaId={praticaId} />;
  }

  return (
    <>
      <Header />
      <main className="min-h-screen bg-[#FCFCFA] pt-20" data-testid="verifica-moduli-page">
        {/* Hero */}
        <section className="bg-gradient-to-br from-[#0F1F14] via-[#163321] to-[#1d4a2e] text-white">
          <div className="max-w-6xl mx-auto px-5 sm:px-6 py-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 border border-white/20 rounded-full text-[10px] font-semibold uppercase tracking-wider text-[#D97B58] mb-3">
              <ShieldCheck className="w-3 h-3" /> Verifica pre-firma
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold leading-tight mb-2">
              Verifica i <span className="text-[#D97B58]">moduli precompilati</span>
            </h1>
            <p className="text-sm text-white/80 max-w-2xl leading-relaxed">
              Abbiamo precompilato i moduli Ayvens con i dati della tua pratica <strong className="text-white">{data?.denominazione}</strong>. Controlla tutto con attenzione prima di passare alla firma elettronica YouSign.
            </p>
          </div>
        </section>

        <div className="max-w-6xl mx-auto px-5 sm:px-6 py-8">
          <div className="grid md:grid-cols-[260px_1fr] gap-6">
            {/* Sidebar elenco moduli */}
            <aside className="space-y-2">
              <p className="text-[10px] uppercase tracking-widest font-bold text-[#163321] mb-2 flex items-center gap-1">
                <FileSignature className="w-3 h-3" /> Moduli da verificare ({data?.moduli?.length || 0})
              </p>
              {(data?.moduli || []).map((m) => {
                const isActive = active === m.key;
                const isChecked = !!checked[m.key];
                return (
                  <button
                    key={m.key}
                    onClick={() => setActive(m.key)}
                    className={`w-full text-left p-3 rounded-xl border transition-all flex items-start gap-2 ${
                      isActive ? "bg-[#163321] text-white border-[#163321]" :
                      isChecked ? "bg-emerald-50 border-emerald-300" :
                      "bg-white border-[#E5E2DB] hover:border-[#163321]/40"
                    }`}
                    data-testid={`modulo-tab-${m.key}`}
                  >
                    <span className={`w-5 h-5 rounded-full inline-flex items-center justify-center shrink-0 mt-0.5 ${
                      isChecked ? "bg-emerald-500 text-white" : isActive ? "bg-white/20 text-white" : "bg-zinc-100 text-zinc-400"
                    }`}>
                      {isChecked ? <CheckCircle2 className="w-3 h-3" /> : <FileText className="w-3 h-3" />}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs font-semibold truncate ${isActive ? "text-white" : "text-[#0F1F14]"}`}>{m.name}</p>
                      <p className={`text-[10px] ${isActive ? "text-white/70" : isChecked ? "text-emerald-700" : "text-[#4A5E51]"}`}>
                        {isChecked ? "Letto ✓" : "Da verificare"}
                      </p>
                    </div>
                  </button>
                );
              })}
            </aside>

            {/* Main preview area */}
            <section>
              {active && (
                <>
                  <div className="bg-white border border-[#E5E2DB] rounded-2xl overflow-hidden">
                    <div className="flex items-center justify-between gap-3 p-4 border-b border-[#E5E2DB]">
                      <p className="text-sm font-semibold text-[#0F1F14] flex items-center gap-2">
                        <Eye className="w-4 h-4 text-[#163321]" />
                        {data.moduli.find(m => m.key === active)?.name}
                      </p>
                      <a
                        href={previewUrl(active)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-[#163321] hover:underline"
                        data-testid={`modulo-open-new-${active}`}
                      >
                        Apri in nuova tab ↗
                      </a>
                    </div>
                    <iframe
                      key={active}
                      src={previewUrl(active)}
                      title={`Anteprima ${active}`}
                      className="w-full bg-zinc-50"
                      style={{ height: "70vh", minHeight: 500, border: 0 }}
                      data-testid="modulo-iframe"
                    />
                    <div className="p-4 border-t border-[#E5E2DB] bg-[#F5F8F6]">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!checked[active]}
                          onChange={() => toggleChecked(active)}
                          className="w-4 h-4 rounded accent-[#163321]"
                          data-testid={`modulo-confirm-${active}`}
                        />
                        <span className="text-xs text-[#0F1F14]">
                          Dichiaro di aver <strong>letto e verificato</strong> i dati del modulo sopra. Confermo che sono corretti.
                        </span>
                      </label>
                    </div>
                  </div>
                </>
              )}

              {/* Confirm */}
              <div className="mt-6 bg-white border border-[#E5E2DB] rounded-2xl p-5">
                <div className="flex items-start gap-3 mb-4">
                  <ShieldCheck className={`w-5 h-5 shrink-0 mt-0.5 ${allChecked ? "text-emerald-600" : "text-zinc-400"}`} />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-[#0F1F14] mb-0.5">
                      {allChecked ? "Tutti i moduli verificati ✓" : `Verifica tutti i ${data?.moduli?.length || 0} moduli per procedere`}
                    </p>
                    <p className="text-xs text-[#4A5E51] leading-relaxed">
                      Dopo la conferma invieremo i moduli a <strong>YouSign</strong> per la firma elettronica a validità legale. Riceverai un'email con il link sicuro.
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    onClick={submit}
                    disabled={!allChecked || confirming}
                    className="rounded-full bg-[#D97B58] hover:bg-[#c56a48] text-white disabled:opacity-50"
                    data-testid="btn-conferma-firma"
                  >
                    {confirming ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Conferma in corso...</>) : (<><Send className="w-4 h-4 mr-2" />Conferma e procedi alla firma</>)}
                  </Button>
                  <Link
                    to="/noleggio"
                    className="inline-flex items-center gap-1 text-xs text-[#4A5E51] hover:text-[#163321] px-3 py-2"
                  >
                    <ArrowLeft className="w-3 h-3" /> Posticipa
                  </Link>
                </div>
              </div>

              <p className="text-[11px] text-[#4A5E51] mt-4 leading-relaxed">
                <strong>Nota:</strong> se trovi un errore, chiudi questa pagina e contatta il nostro consulente al numero{" "}
                <a href="tel:0817611000" className="text-[#163321] underline">081 761 1000</a> o via email a{" "}
                <a href="mailto:info@colucciassicuratori.com" className="text-[#163321] underline">info@colucciassicuratori.com</a>.
                La pratica verrà corretta e rigenereremo i moduli.
              </p>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}

function SuccessConfirm({ praticaId }) {
  return (
    <>
      <Header />
      <main className="min-h-screen bg-[#FCFCFA] pt-24 pb-16">
        <div className="max-w-xl mx-auto px-5 text-center" data-testid="verifica-success">
          <div className="w-16 h-16 rounded-full bg-emerald-500 flex items-center justify-center mx-auto mb-5">
            <CheckCircle2 className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-heading text-3xl text-[#0F1F14] font-medium mb-3">Moduli verificati</h1>
          <p className="text-sm text-[#4A5E51] mb-3 leading-relaxed">
            Pratica <strong>{praticaId}</strong> pronta per la firma elettronica YouSign.
          </p>
          <p className="text-sm text-[#4A5E51] mb-6 leading-relaxed">
            Riceverai via email un link di firma sicuro entro breve. Grazie!
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            <Link to="/noleggio" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#163321] text-white text-xs font-semibold hover:bg-[#1d4a2e] transition-colors">
              Torna al catalogo
            </Link>
            <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#E5E2DB] text-[#4A5E51] text-xs font-semibold hover:border-[#163321] transition-colors">
              Home
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
